import { useEffect, useState } from "react";
import { CircularProgress, List, Stack, Typography } from "@mui/joy";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { SentimentType, ThoughtRecordType } from "api/thoughtRecordApi";
import ThoughtRecordListItem from "./ThoughtRecordListItem";
import AddNewThoughtRecordButton from "./AddNewThoughtRecordButton";
import TabButtonList from "../../common/TabButtonList";
import { taskListAtom } from "recoil/taskListAtom";
import { theme } from "styles/theme";
import useBottomSafePadding from "hooks/useBottomSafePadding";

export default function ThoughtRecordListForMobile({
  thoughtRecordList,
  currentThoughtRecord,
  setCurrentThoughtRecord,
}: {
  thoughtRecordList?: ThoughtRecordType[];
  currentThoughtRecord?: ThoughtRecordType;
  setCurrentThoughtRecord: React.Dispatch<
    React.SetStateAction<ThoughtRecordType | undefined>
  >;
}) {
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";
  const [listData, setListData] = useState<ThoughtRecordType[] | undefined>(
    thoughtRecordList
  );

  const [selectedTab, setSelectedTab] = useState<SentimentType>("negative");
  useEffect(() => {
    if (currentThoughtRecord?.sentiment) {
      setSelectedTab(currentThoughtRecord.sentiment);
    }
  }, [currentThoughtRecord]);

  const [currentTabListData, setCurrentTabListData] = useState<
    ThoughtRecordType[] | undefined
  >(thoughtRecordList?.filter((element) => element.sentiment === selectedTab));

  const taskListState = useRecoilValue(taskListAtom);
  const isPositiveAvailable = taskListState.some(
    (element) => ["7-1-A"].includes(element.taskId) && element.lastDoneAt
  );

  const safePb = useBottomSafePadding("0px");

  useEffect(() => {
    setListData(thoughtRecordList);
  }, [thoughtRecordList]);

  useEffect(() => {
    setCurrentTabListData(
      listData?.filter(
        (element) => !element.sentiment || element.sentiment === selectedTab
      )
    );
  }, [selectedTab, listData]);

  return (
    <Stack
      alignItems="center"
      sx={{
        backgroundColor: theme.vars.palette.Background.Level1,
        flexShrink: 0,
        width: "100%",
        height: "100%",
        // overflow: "scroll",
        transition: "transform 0.4s, width 0.4s",
        scrollPaddingBlockStart: "20px",
        boxSizing: "border-box",
      }}
      className="scrollbar-hide"
    >
      <Stack
        direction={"column"}
        justifyContent={"center"}
        spacing={"12px"}
        sx={{
          width: "100%",
          flexShrink: 0,
          zIndex: 10,
          position: "sticky",
          py: "8px",
          top: 0,
          px: "20px",
        }}
      >
        <Stack
          spacing={1}
          direction={"column"}
          sx={{
            flexShrink: 0,
            width: "100%",
          }}
        >
          <TabButtonList
            selectedTab={selectedTab}
            data={[
              {
                id: "negative",
                label: "부정",
                onClick: () => {
                  setSelectedTab("negative");
                },
              },
              {
                id: "positive",
                label: "긍정",
                onClick: () => {
                  setSelectedTab("positive");
                },
              },
            ]}
          />
          <AddNewThoughtRecordButton
            disabled={
              isCoach ||
              (selectedTab === "positive" && !isPositiveAvailable) ||
              ((listData || []).length === 1 &&
                !listData?.every((element) => element.lastDoneAt))
            }
            sentiment={selectedTab}
          />
        </Stack>
      </Stack>

      <List
        orientation={"vertical"}
        className="scrollbar-hide"
        sx={{
          py: 0,
          px: "20px",
          paddingBottom: safePb,
          overflow: "scroll",
          width: "100%",
          "--ListItem-paddingY": 0,
          "--ListItem-paddingX": 0,
        }}
      >
        {thoughtRecordList === undefined ? (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%", py: 4 }}
          >
            <CircularProgress color={"neutral"} />
          </Stack>
        ) : currentTabListData && currentTabListData.length > 0 ? (
          currentTabListData.map((thoughtRecord, index) => {
            return (
              <ThoughtRecordListItem
                key={thoughtRecord.thoughtRecordKey}
                width={"100%"}
                direction={"column"}
                isFirstRecordOfMonth={
                  index === 0 ||
                  (index - 1 >= 0 &&
                    dayjs(currentTabListData[index - 1].date).get("month") !==
                      dayjs(currentTabListData[index].date).get("month"))
                }
                thoughtRecord={thoughtRecord}
                isSelected={
                  currentThoughtRecord?.thoughtRecordKey ===
                  thoughtRecord.thoughtRecordKey
                }
                setCurrentThoughtRecord={() => {
                  setCurrentThoughtRecord(thoughtRecord);
                }}
                shouldBeDone={!isCoach && thoughtRecord.shouldBeDone}
              />
            );
          })
        ) : (
          <>
            <Typography
              variant={"Caption"}
              sx={{
                width: "100%",
                textAlign: "center",
              }}
            >
              {selectedTab === "positive"
                ? isPositiveAvailable
                  ? "작성한 기록이 없어요."
                  : "아직 배우지 않았어요. 부정 기록에 집중해주세요."
                : "작성한 기록이 없어요."}
            </Typography>
          </>
        )}
      </List>
    </Stack>
  );
}
